/**
 * SignatureCell - the Signature column of Manual Checkout History.
 *
 * (2026-08-13) History used to render `signatureUrl` (a public S3 URL the backend
 * rebuilt from the docNum) directly, so when the server-side upload failed the
 * row showed a broken image with no hint the signature existed on the tablet.
 *
 * (2026-08-20) Signatures now live in a PRIVATE cloud container, so the raw URL
 * cannot be loaded in an <img>. The caller fetches the cloud copy through the
 * authenticated endpoint (see useCloudSignatures) and passes it here as a base64
 * data URL. Order of preference:
 *   1. the CLOUD copy (`cloudSignature`) - the server copy is authoritative and
 *      is what the billing report embeds;
 *   2. the copy retained on THIS device (IndexedDB, see utils/signatureStore),
 *      badged so nobody mistakes a device-only image for one the server has;
 *   3. the legacy typed initials, for tickets predating drawn signatures.
 *
 * (2026-08-20) The thumbnail is CLICKABLE and opens a large view. The row
 * thumbnail is only ~40px tall, which is too small to read a signature or to
 * capture with a phone camera / screenshot when someone needs a copy for a
 * dispute. The large view deliberately shows the image on white with the ticket
 * number beside it, so a photo of the screen is self-identifying.
 */
import React, { useCallback, useEffect, useState } from 'react';

export interface SignatureCellProps {
  /** Data URL of the signature stored in the cloud, fetched via the authed
   *  endpoint. Undefined when the server has no copy for this ticket. */
  cloudSignature?: string;
  /** Data URL retained on this device for this ticket, if any. */
  localSignature?: string;
  /** Legacy typed initials, for tickets with no drawn signature at all. */
  initials?: string;
  /** Ticket number, shown in the enlarged view so a screenshot/photo of it
   *  identifies which checkout the signature belongs to. */
  docNum?: string;
}

export const SignatureCell: React.FC<SignatureCellProps> = ({
  cloudSignature,
  localSignature,
  initials,
  docNum,
}) => {
  const [remoteFailed, setRemoteFailed] = useState(false);
  const [localFailed, setLocalFailed] = useState(false);
  const [enlarged, setEnlarged] = useState(false);

  // A new row (or a freshly fetched image) deserves a fresh attempt at the cloud
  // copy; without this, one decode error would pin the cell to the local image.
  useEffect(() => {
    setRemoteFailed(false);
  }, [cloudSignature]);
  useEffect(() => {
    setLocalFailed(false);
  }, [localSignature]);

  const close = useCallback(() => setEnlarged(false), []);

  // Esc closes the large view. Bound only while open so we don't leave a
  // listener attached for every row in the table.
  useEffect(() => {
    if (!enlarged) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enlarged, close]);

  const useRemote = !!cloudSignature && !remoteFailed;
  const useLocal = !useRemote && !!localSignature && !localFailed;

  if (!useRemote && !useLocal) {
    return <span className="text-secondary-500">{initials || '-'}</span>;
  }

  const src = useRemote ? cloudSignature : localSignature;

  return (
    <div className="flex flex-col items-start gap-1">
      {/* A button, not a bare <img> with onClick: keyboard-focusable and
          announced as activatable, which matters on a shared tablet. */}
      <button
        type="button"
        onClick={() => setEnlarged(true)}
        title="Tap to enlarge"
        aria-label={docNum ? `Enlarge signature for ${docNum}` : 'Enlarge signature'}
        className="p-0 border-0 bg-transparent cursor-zoom-in"
      >
        <img
          // Keyed by source so swapping cloud -> local remounts the <img> rather
          // than reusing the element that just failed to load.
          key={useRemote ? 'cloud' : 'local'}
          src={src}
          alt="Signature"
          onError={() => (useRemote ? setRemoteFailed(true) : setLocalFailed(true))}
          className="h-10 max-w-[140px] object-contain bg-white border border-secondary-200 rounded hover:border-primary-400"
        />
      </button>

      {useLocal && (
        <span
          className="text-[10px] leading-none px-1 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap"
          title="This image is stored on this tablet only - the server copy is missing. Use Export Signatures to send it for billing."
        >
          on this device
        </span>
      )}

      {enlarged && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop: click anywhere outside the image to dismiss. */}
          <div
            className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
            onClick={close}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Signature"
            className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-full overflow-auto"
          >
            <div className="flex items-center justify-between gap-4 px-4 py-2 border-b border-secondary-200">
              <div className="text-sm font-medium text-secondary-700">
                {docNum ? `Signature - ${docNum}` : 'Signature'}
                {useLocal && (
                  <span className="ml-2 text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">
                    on this device
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="text-secondary-500 hover:text-secondary-800 text-2xl leading-none px-2"
              >
                &times;
              </button>
            </div>
            {/* White background and generous size: this view exists so someone
                can read the signature or photograph the screen for a dispute. */}
            <div className="bg-white p-4 flex items-center justify-center">
              <img
                src={src}
                alt={docNum ? `Signature for ${docNum}` : 'Signature'}
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureCell;
